import { useEffect, useRef, useState } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Code,
  Text,
  Group,
  Stack,
} from "@mantine/core";
import type { ElementContext } from "./dev-feedback-plugin";
import styles from "./dev-feedback-modal.module.scss";

interface DevFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  elementContext: ElementContext | null;
}

type IssueType = "Bug" | "Task";

function formatClipboardContent(
  issueType: IssueType,
  summary: string,
  description: string,
  elementContext: ElementContext,
): string {
  const lines = [`[${issueType}] ${summary}`, "", description, "", "---"];

  lines.push("Element Context:");

  if (elementContext.componentName) {
    lines.push(`- Component: ${elementContext.componentName}`);
  }
  if (elementContext.filePath) {
    const location = elementContext.lineNumber
      ? `${elementContext.filePath}:${elementContext.lineNumber}`
      : elementContext.filePath;
    lines.push(`- File: ${location}`);
  }
  if (elementContext.htmlSnippet) {
    lines.push(`- HTML:\n${elementContext.htmlSnippet}`);
  }
  if (elementContext.pageUrl) {
    lines.push(`- Page: ${elementContext.pageUrl}`);
  }

  return lines.join("\n");
}

export function DevFeedbackModal({
  isOpen,
  onClose,
  elementContext,
}: DevFeedbackModalProps) {
  const [issueType, setIssueType] = useState<IssueType>("Bug");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const handleClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSummary("");
    setDescription("");
    setIssueType("Bug");
    setCopied(false);
    setCopyError(null);
    onClose();
  };

  const handleCopy = async () => {
    if (!elementContext) return;
    setCopyError(null);

    const content = formatClipboardContent(
      issueType,
      summary,
      description,
      elementContext,
    );

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      closeTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        handleClose();
      }, 1500);
    } catch {
      setCopyError("Could not copy to clipboard. Please copy manually.");
    }
  };

  const contextLabel = elementContext
    ? [
        elementContext.componentName,
        elementContext.filePath &&
          (elementContext.lineNumber
            ? `${elementContext.filePath}:${elementContext.lineNumber}`
            : elementContext.filePath),
      ]
        .filter(Boolean)
        .join(" in ")
    : "";

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Request Change"
      size="lg"
      classNames={{ title: styles.modalTitle }}
    >
      <Stack gap="md">
        {contextLabel && (
          <Text size="sm" c="dimmed">
            {contextLabel}
          </Text>
        )}

        <Select
          label="Issue Type"
          data={["Bug", "Task"]}
          value={issueType}
          onChange={(value) => setIssueType((value as IssueType) ?? "Bug")}
        />

        <TextInput
          label="Summary"
          placeholder="Brief title for the issue"
          value={summary}
          onChange={(e) => setSummary(e.currentTarget.value)}
        />

        <Textarea
          label="Description"
          placeholder="Describe the change you'd like to see"
          minRows={3}
          autosize
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        {elementContext?.htmlSnippet && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Element HTML
            </Text>
            <Code block className={styles.codeBlock}>
              {elementContext.htmlSnippet}
            </Code>
          </div>
        )}

        {copyError && (
          <Text size="sm" c="red">
            {copyError}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!summary.trim()}
            color={copied ? "green" : undefined}
          >
            {copied ? "Copied!" : "Copy to Clipboard"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
